import React, { useEffect, useRef, useState } from 'react';
import gsap from 'gsap';

/**
 * Full-screen loader shown for a beat on first load, then wipes away
 * with a GSAP timeline, revealing the site underneath. Calls
 * `onComplete` once the exit animation finishes so you can, e.g.,
 * kick off the Home entrance animation right after.
 */
export default function Preloader({ onComplete }) {
  const wrapRef = useRef(null);
  const barRef = useRef(null);
  const labelRef = useRef(null);
  const panelTopRef = useRef(null);
  const panelBottomRef = useRef(null);
  const [percent, setPercent] = useState(0);

  useEffect(() => {
    const tl = gsap.timeline();
    const counter = { value: 0 };

    tl.to(counter, {
      value: 100,
      duration: 1.3,
      ease: 'power2.inOut',
      onUpdate: () => setPercent(Math.round(counter.value)),
    })
      .to(labelRef.current, { opacity: 0, duration: 0.3 }, '+=0.1')
      .to(barRef.current, { scaleX: 0, transformOrigin: 'right', duration: 0.4 }, '<')
      .to(
        [panelTopRef.current, panelBottomRef.current],
        {
          duration: 0.9,
          ease: 'power4.inOut',
          yPercent: (i) => (i === 0 ? -100 : 100),
        },
        '+=0.05'
      )
      .set(wrapRef.current, { display: 'none' })
      .call(() => onComplete && onComplete());

    return () => tl.kill();
  }, [onComplete]);

  return (
    <div
      ref={wrapRef}
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 10000,
        pointerEvents: 'none',
      }}
    >
      <div
        ref={panelTopRef}
        style={{
          position: 'absolute',
          inset: 0,
          height: '50%',
          background: '#04091a',
          display: 'flex',
          alignItems: 'flex-end',
          justifyContent: 'center',
          paddingBottom: 12,
        }}
      >
        <span
          ref={labelRef}
          style={{
            fontFamily: 'inherit',
            fontWeight: 800,
            fontSize: '0.95rem',
            letterSpacing: '0.25em',
            color: '#f8fafc',
          }}
        >
          SYED SAAD WAQAR
        </span>
      </div>
      <div
        ref={panelBottomRef}
        style={{
          position: 'absolute',
          bottom: 0,
          left: 0,
          right: 0,
          height: '50%',
          background: '#04091a',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'flex-start',
          paddingTop: 10,
        }}
      >
        <div
          style={{
            width: 160,
            height: 2,
            background: 'rgba(129,140,248,0.2)',
            position: 'relative',
            overflow: 'hidden',
          }}
        >
          <div
            ref={barRef}
            style={{
              position: 'absolute',
              inset: 0,
              background:
                'linear-gradient(90deg, #6366f1, #06b6d4)',
              transform: `scaleX(${percent / 100})`,
              transformOrigin: 'left',
            }}
          />
        </div>
        <span
          style={{
            marginTop: 10,
            fontSize: '0.7rem',
            letterSpacing: '0.2em',
            color: '#64748b',
            fontWeight: 600,
          }}
        >
          {percent}%
        </span>
      </div>
    </div>
  );
}
